import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { toast } from "sonner";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
} from "recharts";
import {
  Wallet,
  TrendUp,
  TrendDown,
  Gear,
  SignOut,
  Play,
  Stop,
  ArrowsClockwise,
  Lightning,
  ShieldCheck,
  Warning,
  ChartLine,
  ListBullets,
  User,
  CaretDown,
  X,
  Info,
  CheckCircle,
  XCircle,
  FileText,
  CurrencyDollar,
  Bank,
  CaretRight,
} from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";

export default function Dashboard() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [conditions, setConditions] = useState([]);
  const [botStatus, setBotStatus] = useState({ running: false });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [tradeDetailOpen, setTradeDetailOpen] = useState(false);
  
  // New state for exchange features
  const [preferredExchange, setPreferredExchange] = useState("binance");
  const [exchangeBalances, setExchangeBalances] = useState({});
  const [exchangePositions, setExchangePositions] = useState([]);
  const [reportOpen, setReportOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [lastConditionsUpdate, setLastConditionsUpdate] = useState(null);
  const [conditionsLoading, setConditionsLoading] = useState(false);
  const [webhookConfig, setWebhookConfig] = useState(null);

  // Fetch market conditions separately for more frequent updates
  const fetchConditions = useCallback(async () => {
    setConditionsLoading(true);
    try {
      const condRes = await api.get("/trading/conditions");
      setConditions(condRes.data.conditions || []);
      setLastConditionsUpdate(new Date());
    } catch (e) {
      console.error("Failed to fetch conditions:", e);
    } finally {
      setConditionsLoading(false);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, perfRes, condRes, botRes, exchRes, exchBalRes, exchPosRes, webhookRes] = await Promise.all([
        api.get("/trading/dashboard"),
        api.get("/trading/performance"),
        api.get("/trading/conditions").catch(() => ({ data: { conditions: [] } })),
        api.get("/bot/status"),
        api.get("/user/preferred-exchange").catch(() => ({ data: { exchange: "binance" } })),
        api.get("/exchange/balance").catch(() => ({ data: { balances: {} } })),
        api.get("/exchange/positions").catch(() => ({ data: { positions: [] } })),
        api.get("/user/webhook-config").catch(() => ({ data: { configured: false } })),
      ]);
      setDashboard(dashRes.data);
      setPerformance(perfRes.data);
      setConditions(condRes.data.conditions || []);
      setLastConditionsUpdate(new Date());
      setBotStatus(botRes.data);
      setPreferredExchange(exchRes.data.exchange || "binance");
      setExchangeBalances(exchBalRes.data.balances || {});
      setExchangePositions(exchPosRes.data.positions || []);
      setWebhookConfig(webhookRes.data);
      
      // If Bybit has webhook configured but balance failed, try webhook balance
      const balances = exchBalRes.data.balances || {};
      if (balances.bybit && !balances.bybit.connected && webhookRes.data.configured) {
        try {
          const webhookBalRes = await api.get("/trading/webhook-balance");
          if (webhookBalRes.data.success) {
            setExchangeBalances(prev => ({
              ...prev,
              bybit: {
                connected: true,
                usdt: webhookBalRes.data.balance,
                mode: prev.bybit?.mode || 'live',
                source: 'webhook'
              }
            }));
          }
        } catch (e) {
          console.log("Webhook balance fetch failed:", e);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Main data refresh every 60 seconds
    const mainInterval = setInterval(fetchData, 60000);
    // Market conditions refresh every 30 seconds
    const conditionsInterval = setInterval(fetchConditions, 30000);
    return () => {
      clearInterval(mainInterval);
      clearInterval(conditionsInterval);
    };
  }, [fetchData, fetchConditions]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    await refreshUser();
    toast.success("Data refreshed");
  };

  const handleBotToggle = async () => {
    try {
      if (botStatus.running) {
        await api.post("/bot/stop");
        toast.success("Bot stopped");
      } else {
        await api.post("/bot/start");
        toast.success("Bot started");
      }
      const res = await api.get("/bot/status");
      setBotStatus(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to toggle bot");
    }
  };

  const handleModeToggle = async (checked) => {
    try {
      await api.put("/user/trading-mode", { mode: checked ? "live" : "paper" });
      await refreshUser();
      await fetchData();
      toast.success(`Switched to ${checked ? "Live" : "Paper"} trading`);
    } catch (e) {
      toast.error("Failed to switch mode");
    }
  };

  const handleExchangeChange = async (exchange) => {
    try {
      await api.put("/user/preferred-exchange", { exchange });
      setPreferredExchange(exchange);
      toast.success(`Trading exchange set to ${exchange.toUpperCase()}`);
    } catch (e) {
      toast.error("Failed to set exchange");
    }
  };

  const handleClosePosition = async (positionId) => {
    try {
      const result = await api.post(`/trading/close-position/${positionId}?close_reason=Manual%20close`);
      toast.success(`Position closed: ${result.data.result}`);
      fetchData();
      refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to close position");
    }
  };

  const handleTradeClick = async (trade) => {
    try {
      const res = await api.get(`/trading/trade/${trade.id}`);
      setSelectedTrade(res.data.trade);
      setTradeDetailOpen(true);
    } catch (e) {
      setSelectedTrade(trade);
      setTradeDetailOpen(true);
    }
  };

  const handleGenerateReport = async () => {
    setLoadingReport(true);
    try {
      const res = await api.get("/trading/report");
      setReport(res.data);
      setReportOpen(true);
    } catch (e) {
      toast.error("Failed to generate report");
    } finally {
      setLoadingReport(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="text-center">
          <div className="w-6 h-6 border-2 border-[#111] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-xs text-[#666] tracking-wide">LOADING</p>
        </div>
      </div>
    );
  }

  const isLive = user?.trading_mode === "live";
  const pnlPositive = (dashboard?.total_pnl || 0) >= 0;
  const currentExchangeBalance = exchangeBalances[preferredExchange];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-[1400px] mx-auto px-6 h-12 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <h1 className="font-bold text-sm tracking-tight">VAA</h1>
            
            {/* Exchange Selector */}
            <div className="flex items-center gap-2">
              <Bank size={14} className="text-[#999]" />
              <Select value={preferredExchange} onValueChange={handleExchangeChange}>
                <SelectTrigger className="w-[100px] h-7 text-xs border-[#E5E5E5]" data-testid="exchange-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="binance">Binance</SelectItem>
                  <SelectItem value="bybit">Bybit</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Mode Toggle */}
            <div className="flex items-center gap-2">
              <span className={`text-[10px] tracking-widest font-medium px-2 py-1 ${
                isLive ? "bg-red-50 text-red-600" : "bg-blue-50 text-blue-600"
              }`}>
                {isLive ? "LIVE" : "PAPER"}
              </span>
              <Switch
                data-testid="mode-toggle"
                checked={isLive}
                onCheckedChange={handleModeToggle}
                className="scale-75"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Report Button */}
            <button
              data-testid="report-btn"
              onClick={handleGenerateReport}
              disabled={loadingReport}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#111] text-white hover:bg-[#333] transition-colors disabled:opacity-50"
            >
              {loadingReport ? (
                <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <FileText size={14} />
              )}
              Report
            </button>

            <button
              data-testid="refresh-btn"
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 hover:bg-[#F5F5F5] rounded-sm transition-colors"
            >
              <ArrowsClockwise size={16} className={refreshing ? "animate-spin" : ""} />
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="user-menu-trigger"
                className="flex items-center gap-2 px-3 py-1.5 hover:bg-[#F5F5F5] rounded-sm transition-colors"
              >
                <span className="text-xs font-medium">{user?.name}</span>
                <CaretDown size={12} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem
                  data-testid="settings-link"
                  onClick={() => navigate("/settings")}
                  className="cursor-pointer text-xs"
                >
                  <Gear size={14} className="mr-2" />
                  Settings
                </DropdownMenuItem>
                {user?.role === "admin" && (
                  <DropdownMenuItem
                    data-testid="admin-link"
                    onClick={() => navigate("/admin")}
                    className="cursor-pointer text-xs"
                  >
                    <ShieldCheck size={14} className="mr-2" />
                    Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="logout-btn"
                  onClick={logout}
                  className="cursor-pointer text-xs text-red-600"
                >
                  <SignOut size={14} className="mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Circuit Breaker Warning */}
      {dashboard?.circuit_breaker_active && (
        <div className="bg-red-500 text-white px-6 py-2 text-xs font-medium flex items-center justify-center gap-2">
          <Warning size={14} />
          CIRCUIT BREAKER: Balance below $500
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-[1400px] mx-auto p-6">
        {/* MARKET ANALYSIS - Prominent Section at Top */}
        <div className="bg-white border border-[#E5E5E5] mb-6" data-testid="market-analysis-panel">
          <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Lightning size={18} className="text-amber-500" />
              <span className="text-sm font-semibold tracking-wide">LIVE MARKET ANALYSIS</span>
              {conditionsLoading && (
                <div className="w-3 h-3 border border-blue-500 border-t-transparent rounded-full animate-spin" />
              )}
            </div>
            <div className="flex items-center gap-3">
              {lastConditionsUpdate && (
                <span className="text-[10px] text-[#999]">
                  Updated: {lastConditionsUpdate.toLocaleTimeString()}
                </span>
              )}
              <button
                onClick={fetchConditions}
                disabled={conditionsLoading}
                className="text-[10px] px-2 py-1 bg-[#F5F5F5] hover:bg-[#E5E5E5] transition-colors disabled:opacity-50"
              >
                Refresh
              </button>
            </div>
          </div>
          
          {conditions.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 divide-x divide-y divide-[#E5E5E5]">
              {conditions.map((c, i) => (
                <div key={i} className="p-5 hover:bg-[#FAFAFA] transition-colors">
                  {/* Header with Symbol and Signal */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold">{c.symbol}</span>
                      {c.entry_signal && (
                        <span className={`text-[10px] tracking-wider px-2 py-1 animate-pulse ${
                          c.entry_signal === "long" 
                            ? "bg-emerald-500 text-white" 
                            : "bg-red-500 text-white"
                        }`}>
                          {c.entry_signal.toUpperCase()} SIGNAL
                        </span>
                      )}
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 ${
                      c.trend === "bullish" 
                        ? "bg-emerald-50 text-emerald-600 border border-emerald-200" 
                        : "bg-red-50 text-red-600 border border-red-200"
                    }`}>
                      {c.trend === "bullish" ? "↑" : "↓"} {c.trend?.toUpperCase()}
                    </span>
                  </div>
                  
                  {/* Price Display */}
                  <div className="mb-4">
                    <p className="text-3xl font-bold tracking-tight">
                      ${c.current_price?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  
                  {/* Analysis Details */}
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="p-2 bg-[#F5F5F5] rounded">
                      <p className="text-[10px] text-[#999] mb-1">EMA 20 (4H)</p>
                      <p className="font-mono font-medium">
                        ${c.ema_20?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="p-2 bg-[#F5F5F5] rounded">
                      <p className="text-[10px] text-[#999] mb-1">PRICE VS EMA</p>
                      <p className={`font-mono font-medium ${
                        c.current_price > c.ema_20 ? "text-emerald-600" : "text-red-600"
                      }`}>
                        {c.current_price > c.ema_20 ? "+" : ""}
                        {(((c.current_price - c.ema_20) / c.ema_20) * 100).toFixed(2)}%
                      </p>
                    </div>
                  </div>
                  
                  {/* Signal Reason */}
                  {c.reason && (
                    <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded">
                      <p className="text-[10px] text-blue-800">{c.reason}</p>
                    </div>
                  )}
                  
                  {/* No Signal Info */}
                  {!c.entry_signal && (
                    <div className="mt-3 p-2 bg-[#F5F5F5] rounded">
                      <p className="text-[10px] text-[#999]">
                        Waiting for {c.trend === "bullish" ? "pullback entry (green after red)" : "pullback entry (red after green)"}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Lightning size={32} className="mx-auto mb-3 text-[#CCC]" />
              <p className="text-sm text-[#999] mb-2">No market data available</p>
              <p className="text-xs text-[#999]">
                Configure your API keys in <span className="text-blue-500 cursor-pointer hover:underline" onClick={() => navigate('/settings')}>Settings</span> to see live market analysis
              </p>
            </div>
          )}
          
          {/* Auto-refresh indicator */}
          <div className="px-5 py-2 bg-[#FAFAFA] border-t border-[#E5E5E5] flex items-center justify-between">
            <p className="text-[10px] text-[#999]">
              Auto-refreshes every 30 seconds • Strategy: 4H EMA Trend + 30m Pullback Entry
            </p>
            <p className="text-[10px] text-[#999]">
              {conditions.length} pair{conditions.length !== 1 ? 's' : ''} monitored
            </p>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          {/* Local Balance */}
          <div className="bg-white border border-[#E5E5E5] p-5" data-testid="equity-panel">
            <p className="text-[10px] tracking-widest text-[#999] mb-2">LOCAL EQUITY</p>
            <p className="text-2xl font-bold tracking-tight">
              ${dashboard?.equity?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || "0.00"}
            </p>
          </div>

          {/* Exchange Balance */}
          <div className="bg-white border border-[#E5E5E5] p-5" data-testid="exchange-balance-panel">
            <p className="text-[10px] tracking-widest text-[#999] mb-2">
              {preferredExchange.toUpperCase()} BALANCE
              {currentExchangeBalance?.source && currentExchangeBalance.source !== 'direct_api' && (
                <span className="ml-2 text-amber-500">
                  ({currentExchangeBalance.source === 'last_execution' ? 'last trade' : 
                    currentExchangeBalance.source === 'webhook_live' ? 'live' : 
                    currentExchangeBalance.source === 'manual' ? 'manual' : 'webhook'})
                </span>
              )}
            </p>
            {currentExchangeBalance?.connected ? (
              <>
                <p className="text-2xl font-bold tracking-tight">
                  ${currentExchangeBalance?.usdt?.total?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || "0.00"}
                </p>
                <p className="text-[10px] text-[#999] mt-1">
                  {currentExchangeBalance?.queried_at ? (
                    <span>Updated: {new Date(currentExchangeBalance.queried_at).toLocaleTimeString()}</span>
                  ) : currentExchangeBalance?.updated_at ? (
                    <span>Set: {new Date(currentExchangeBalance.updated_at).toLocaleTimeString()}</span>
                  ) : (
                    <span>Free: ${currentExchangeBalance?.usdt?.free?.toFixed(2) || "0.00"}</span>
                  )}
                  {preferredExchange === 'bybit' && (
                    <span className="ml-2 text-blue-500">• CoinGecko data</span>
                  )}
                </p>
              </>
            ) : currentExchangeBalance?.error ? (
              <div>
                <p className="text-sm text-red-500">Connection Error</p>
                <p className="text-[10px] text-red-400 mt-1 truncate" title={currentExchangeBalance.error}>
                  {currentExchangeBalance.error.includes('geo-restricted') || currentExchangeBalance.error.includes('Access denied') 
                    ? 'Configure webhook in Settings' 
                    : 'Check API keys'}
                </p>
              </div>
            ) : (
              <div>
                <p className="text-sm text-[#999]">Not configured</p>
                <p className="text-[10px] text-blue-500 mt-1 cursor-pointer hover:underline" onClick={() => navigate('/settings')}>
                  {preferredExchange === 'bybit' ? 'Add webhook →' : 'Add API keys →'}
                </p>
              </div>
            )}
          </div>

          {/* Total P/L */}
          <div className="bg-white border border-[#E5E5E5] p-5" data-testid="total-pnl-panel">
            <p className="text-[10px] tracking-widest text-[#999] mb-2">TOTAL P/L</p>
            <p className={`text-2xl font-bold tracking-tight ${pnlPositive ? "text-emerald-600" : "text-red-600"}`}>
              {pnlPositive ? "+" : ""}${dashboard?.total_pnl?.toFixed(2) || "0.00"}
            </p>
            <p className={`text-[10px] mt-1 ${pnlPositive ? "text-emerald-600" : "text-red-600"}`}>
              {pnlPositive ? "+" : ""}{dashboard?.total_pnl_percent?.toFixed(2) || "0.00"}%
            </p>
          </div>

          {/* Win Rate */}
          <div className="bg-white border border-[#E5E5E5] p-5" data-testid="win-rate-panel">
            <p className="text-[10px] tracking-widest text-[#999] mb-2">WIN RATE</p>
            <p className="text-2xl font-bold tracking-tight">
              {dashboard?.win_rate?.toFixed(0) || "0"}%
            </p>
            <p className="text-[10px] text-[#999] mt-1">
              {dashboard?.wins || 0}W · {dashboard?.losses || 0}L
            </p>
          </div>

          {/* Bot Status */}
          <div className="bg-white border border-[#E5E5E5] p-5" data-testid="bot-status-panel">
            <p className="text-[10px] tracking-widest text-[#999] mb-2">BOT STATUS</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${botStatus.running ? "bg-emerald-500" : "bg-[#CCC]"}`}></span>
                <span className="text-sm font-medium">{botStatus.running ? "Active" : "Idle"}</span>
              </div>
              <button
                data-testid="bot-toggle-btn"
                onClick={handleBotToggle}
                disabled={dashboard?.circuit_breaker_active}
                className={`p-2 rounded-sm transition-colors ${
                  botStatus.running
                    ? "bg-red-50 text-red-600 hover:bg-red-100"
                    : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                } disabled:opacity-40`}
              >
                {botStatus.running ? <Stop size={16} /> : <Play size={16} />}
              </button>
            </div>
          </div>
        </div>

        {/* Exchange Positions - Real positions from connected exchanges */}
        {exchangePositions.length > 0 && (
          <div className="bg-white border border-[#E5E5E5] mb-6" data-testid="exchange-positions-panel">
            <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bank size={14} />
                <span className="text-xs font-medium tracking-wide">EXCHANGE POSITIONS (LIVE)</span>
              </div>
              <span className="text-[10px] text-[#999]">Real-time from connected exchanges</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-[10px] tracking-wider text-[#999] border-b border-[#F0F0F0]">
                    <th className="text-left px-5 py-3 font-medium">EXCHANGE</th>
                    <th className="text-left px-3 py-3 font-medium">SYMBOL</th>
                    <th className="text-left px-3 py-3 font-medium">SIDE</th>
                    <th className="text-right px-3 py-3 font-medium">SIZE</th>
                    <th className="text-right px-3 py-3 font-medium">ENTRY</th>
                    <th className="text-right px-3 py-3 font-medium">MARK</th>
                    <th className="text-right px-3 py-3 font-medium">LEVERAGE</th>
                    <th className="text-right px-5 py-3 font-medium">UNREALIZED P/L</th>
                  </tr>
                </thead>
                <tbody className="text-xs">
                  {exchangePositions.map((pos, i) => (
                    <tr key={i} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]">
                      <td className="px-5 py-3">
                        <span className="text-[10px] tracking-wider px-2 py-0.5 bg-[#F0F0F0]">
                          {pos.exchange.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-3 py-3 font-medium">{pos.symbol}</td>
                      <td className="px-3 py-3">
                        <span className={`text-[10px] tracking-wider px-2 py-0.5 ${
                          pos.side === "long"
                            ? "bg-emerald-50 text-emerald-600"
                            : "bg-red-50 text-red-600"
                        }`}>
                          {pos.side.toUpperCase()}
                        </span>
                      </td>
                      <td className="text-right px-3 py-3 font-mono">{pos.contracts}</td>
                      <td className="text-right px-3 py-3 font-mono">${pos.entry_price?.toFixed(2)}</td>
                      <td className="text-right px-3 py-3 font-mono">${pos.mark_price?.toFixed(2)}</td>
                      <td className="text-right px-3 py-3">{pos.leverage}x</td>
                      <td className={`text-right px-5 py-3 font-mono font-medium ${
                        pos.unrealized_pnl >= 0 ? "text-emerald-600" : "text-red-600"
                      }`}>
                        ${pos.unrealized_pnl?.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Charts & Conditions */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {/* Balance Chart */}
          <div className="col-span-2 bg-white border border-[#E5E5E5]" data-testid="balance-chart">
            <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center gap-2">
              <ChartLine size={14} />
              <span className="text-xs font-medium tracking-wide">BALANCE HISTORY</span>
            </div>
            <div className="p-4 h-[260px]">
              {performance?.balance_history?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={performance.balance_history}>
                    <defs>
                      <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#111" stopOpacity={0.08} />
                        <stop offset="95%" stopColor="#111" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#999" }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: "#999" }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
                    <Tooltip
                      contentStyle={{ fontSize: 11, border: "1px solid #E5E5E5", borderRadius: 0, boxShadow: "none" }}
                      formatter={(value) => [`$${value.toFixed(2)}`, "Balance"]}
                    />
                    <Area type="monotone" dataKey="balance" stroke="#111" strokeWidth={1.5} fill="url(#balanceGradient)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-[#999]">
                  <p className="text-xs">No trade history yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Market Conditions */}
          <div className="bg-white border border-[#E5E5E5]" data-testid="market-conditions">
            <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center gap-2">
              <Lightning size={14} />
              <span className="text-xs font-medium tracking-wide">MARKET CONDITIONS</span>
            </div>
            <div className="p-4 space-y-3 max-h-[260px] overflow-y-auto">
              {conditions.length > 0 ? (
                conditions.map((c, i) => (
                  <div key={i} className="pb-3 border-b border-[#F0F0F0] last:border-0">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium">{c.symbol}</span>
                      <span className={`text-[10px] tracking-wider px-2 py-0.5 ${
                        c.trend === "bullish" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"
                      }`}>
                        {c.trend?.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-[10px] text-[#999]">${c.current_price?.toFixed(2)} · EMA ${c.ema_20?.toFixed(2)}</p>
                    {c.entry_signal && (
                      <p className="text-[10px] text-blue-600 mt-1 font-medium">⚡ {c.entry_signal.toUpperCase()} SIGNAL</p>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-xs text-[#999] text-center py-6">Configure API keys to view</p>
              )}
            </div>
          </div>
        </div>

        {/* Bot Positions (Local tracking) */}
        <div className="bg-white border border-[#E5E5E5] mb-6" data-testid="positions-panel">
          <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendUp size={14} />
              <span className="text-xs font-medium tracking-wide">BOT POSITIONS (LOCAL TRACKING)</span>
            </div>
            {dashboard?.positions?.length > 0 && (
              <span className={`text-xs font-medium ${dashboard?.unrealized_pnl >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                Unrealized: ${dashboard?.unrealized_pnl?.toFixed(2) || "0.00"}
              </span>
            )}
          </div>
          <div className="overflow-x-auto">
            {dashboard?.positions?.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="text-[10px] tracking-wider text-[#999] border-b border-[#F0F0F0]">
                    <th className="text-left px-5 py-3 font-medium">SYMBOL</th>
                    <th className="text-left px-3 py-3 font-medium">SIDE</th>
                    <th className="text-right px-3 py-3 font-medium">ENTRY</th>
                    <th className="text-right px-3 py-3 font-medium">CURRENT</th>
                    <th className="text-right px-3 py-3 font-medium">QTY</th>
                    <th className="text-right px-3 py-3 font-medium">LEV</th>
                    <th className="text-right px-3 py-3 font-medium">SL</th>
                    <th className="text-right px-3 py-3 font-medium">TP1</th>
                    <th className="text-right px-3 py-3 font-medium">P/L</th>
                    <th className="text-center px-5 py-3 font-medium">ACTION</th>
                  </tr>
                </thead>
                <tbody className="text-xs">
                  {dashboard.positions.map((pos) => (
                    <tr key={pos.id} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]" data-testid={`position-row-${pos.symbol}`}>
                      <td className="px-5 py-3 font-medium">{pos.symbol}</td>
                      <td className="px-3 py-3">
                        <span className={`text-[10px] tracking-wider px-2 py-0.5 ${
                          pos.side === "long" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"
                        }`}>
                          {pos.side.toUpperCase()}
                        </span>
                      </td>
                      <td className="text-right px-3 py-3 font-mono">${pos.entry_price?.toFixed(2)}</td>
                      <td className="text-right px-3 py-3 font-mono">${pos.current_price?.toFixed(2)}</td>
                      <td className="text-right px-3 py-3 font-mono">{pos.quantity?.toFixed(4)}</td>
                      <td className="text-right px-3 py-3">{pos.leverage}x</td>
                      <td className="text-right px-3 py-3 font-mono">${pos.stop_loss?.toFixed(2)}</td>
                      <td className="text-right px-3 py-3 font-mono">${pos.take_profit_1?.toFixed(2)}</td>
                      <td className={`text-right px-3 py-3 font-mono font-medium ${
                        pos.unrealized_pnl >= 0 ? "text-emerald-600" : "text-red-600"
                      }`}>
                        ${pos.unrealized_pnl?.toFixed(2)}
                      </td>
                      <td className="text-center px-5 py-3">
                        <button
                          data-testid={`close-position-${pos.id}`}
                          onClick={() => handleClosePosition(pos.id)}
                          className="px-3 py-1 text-[10px] tracking-wider bg-[#111] text-white hover:bg-[#333] transition-colors"
                        >
                          CLOSE
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="px-5 py-8 text-center">
                <p className="text-xs text-[#999]">No active positions</p>
              </div>
            )}
          </div>
        </div>

        {/* Trade Log */}
        <div className="bg-white border border-[#E5E5E5]" data-testid="trade-log-panel">
          <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center gap-2">
            <ListBullets size={14} />
            <span className="text-xs font-medium tracking-wide">TRADE HISTORY</span>
            <span className="text-[10px] text-[#999] ml-2">Click for details</span>
          </div>
          <div className="overflow-x-auto max-h-[320px]">
            {dashboard?.recent_trades?.length > 0 ? (
              <table className="w-full">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-[10px] tracking-wider text-[#999] border-b border-[#F0F0F0]">
                    <th className="text-left px-5 py-3 font-medium">DATE</th>
                    <th className="text-left px-3 py-3 font-medium">SYMBOL</th>
                    <th className="text-left px-3 py-3 font-medium">SIDE</th>
                    <th className="text-right px-3 py-3 font-medium">ENTRY</th>
                    <th className="text-right px-3 py-3 font-medium">EXIT</th>
                    <th className="text-right px-3 py-3 font-medium">P/L</th>
                    <th className="text-center px-3 py-3 font-medium">RESULT</th>
                    <th className="text-left px-5 py-3 font-medium">REASON</th>
                  </tr>
                </thead>
                <tbody className="text-xs">
                  {dashboard.recent_trades.map((trade) => (
                    <tr 
                      key={trade.id} 
                      className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA] cursor-pointer transition-colors" 
                      data-testid={`trade-row-${trade.id}`}
                      onClick={() => handleTradeClick(trade)}
                    >
                      <td className="px-5 py-3 text-[#999]">{new Date(trade.opened_at).toLocaleDateString()}</td>
                      <td className="px-3 py-3 font-medium">{trade.symbol}</td>
                      <td className="px-3 py-3">
                        <span className={`text-[10px] tracking-wider px-2 py-0.5 ${
                          trade.side === "long" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"
                        }`}>
                          {trade.side.toUpperCase()}
                        </span>
                      </td>
                      <td className="text-right px-3 py-3 font-mono">${trade.entry_price?.toFixed(2)}</td>
                      <td className="text-right px-3 py-3 font-mono">{trade.exit_price ? `$${trade.exit_price.toFixed(2)}` : "—"}</td>
                      <td className={`text-right px-3 py-3 font-mono font-medium ${
                        trade.status === "closed" ? trade.pnl >= 0 ? "text-emerald-600" : "text-red-600" : "text-[#999]"
                      }`}>
                        {trade.status === "closed" ? `$${trade.pnl?.toFixed(2)}` : "—"}
                      </td>
                      <td className="text-center px-3 py-3">
                        {trade.status === "closed" ? (
                          trade.result === "WIN" ? (
                            <CheckCircle size={16} className="inline text-emerald-500" />
                          ) : trade.result === "LOSS" ? (
                            <XCircle size={16} className="inline text-red-500" />
                          ) : (
                            <span className="text-[10px] bg-[#F0F0F0] px-2 py-0.5">EVEN</span>
                          )
                        ) : (
                          <span className="text-[10px] bg-amber-50 text-amber-600 px-2 py-0.5">OPEN</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-[#999] max-w-[200px] truncate">{trade.reason?.substring(0, 40)}...</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="px-5 py-8 text-center">
                <p className="text-xs text-[#999]">No trades yet</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Trade Detail Dialog */}
      <Dialog open={tradeDetailOpen} onOpenChange={setTradeDetailOpen}>
        <DialogContent className="max-w-lg bg-white border border-[#E5E5E5] rounded-none shadow-xl">
          <DialogHeader className="border-b border-[#E5E5E5] pb-4">
            <DialogTitle className="flex items-center gap-3">
              <span className="text-sm font-bold">{selectedTrade?.symbol}</span>
              <span className={`text-[10px] tracking-wider px-2 py-0.5 ${
                selectedTrade?.side === "long" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"
              }`}>
                {selectedTrade?.side?.toUpperCase()}
              </span>
              {selectedTrade?.status === "closed" && (
                <span className={`text-[10px] tracking-wider px-2 py-0.5 ${
                  selectedTrade?.result === "WIN" ? "bg-emerald-500 text-white"
                    : selectedTrade?.result === "LOSS" ? "bg-red-500 text-white" : "bg-[#999] text-white"
                }`}>
                  {selectedTrade?.result || "CLOSED"}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTrade && (
            <div className="space-y-4 pt-2">
              <div className="bg-[#FAFAFA] p-4 border border-[#E5E5E5]">
                <p className="text-[10px] tracking-widest text-[#999] mb-2">ENTRY REASON</p>
                <p className="text-xs leading-relaxed">{selectedTrade.reason || "Manual entry"}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 border border-[#E5E5E5]">
                  <p className="text-[10px] tracking-widest text-[#999] mb-1">ENTRY PRICE</p>
                  <p className="text-sm font-mono font-medium">${selectedTrade.entry_price?.toFixed(2)}</p>
                </div>
                <div className="p-3 border border-[#E5E5E5]">
                  <p className="text-[10px] tracking-widest text-[#999] mb-1">EXIT PRICE</p>
                  <p className="text-sm font-mono font-medium">{selectedTrade.exit_price ? `$${selectedTrade.exit_price.toFixed(2)}` : "—"}</p>
                </div>
                <div className="p-3 border border-[#E5E5E5]">
                  <p className="text-[10px] tracking-widest text-[#999] mb-1">QUANTITY</p>
                  <p className="text-sm font-mono font-medium">{selectedTrade.quantity?.toFixed(6)}</p>
                </div>
                <div className="p-3 border border-[#E5E5E5]">
                  <p className="text-[10px] tracking-widest text-[#999] mb-1">LEVERAGE</p>
                  <p className="text-sm font-medium">{selectedTrade.leverage}x</p>
                </div>
                <div className="p-3 border border-[#E5E5E5]">
                  <p className="text-[10px] tracking-widest text-[#999] mb-1">STOP LOSS</p>
                  <p className="text-sm font-mono font-medium">${selectedTrade.stop_loss?.toFixed(2)}</p>
                </div>
                <div className="p-3 border border-[#E5E5E5]">
                  <p className="text-[10px] tracking-widest text-[#999] mb-1">TAKE PROFIT 1</p>
                  <p className="text-sm font-mono font-medium">${selectedTrade.take_profit_1?.toFixed(2)}</p>
                </div>
              </div>

              {selectedTrade.status === "closed" && (
                <div className={`p-4 border ${selectedTrade.pnl >= 0 ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}>
                  <p className="text-[10px] tracking-widest text-[#999] mb-2">PROFIT / LOSS</p>
                  <p className={`text-xl font-bold ${selectedTrade.pnl >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {selectedTrade.pnl >= 0 ? "+" : ""}${selectedTrade.pnl?.toFixed(2)}
                    <span className="text-sm ml-2">({selectedTrade.pnl_percent?.toFixed(2)}%)</span>
                  </p>
                </div>
              )}

              {selectedTrade.close_explanation && (
                <div className="bg-[#FAFAFA] p-4 border border-[#E5E5E5]">
                  <p className="text-[10px] tracking-widest text-[#999] mb-2">CLOSE EXPLANATION</p>
                  <p className="text-xs leading-relaxed">{selectedTrade.close_explanation}</p>
                </div>
              )}

              <div className="flex justify-between text-[10px] text-[#999] pt-2 border-t border-[#E5E5E5]">
                <span>Opened: {new Date(selectedTrade.opened_at).toLocaleString()}</span>
                {selectedTrade.closed_at && <span>Closed: {new Date(selectedTrade.closed_at).toLocaleString()}</span>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-2xl bg-white border border-[#E5E5E5] rounded-none shadow-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="border-b border-[#E5E5E5] pb-4">
            <DialogTitle className="flex items-center gap-2">
              <FileText size={18} />
              <span>Trading Report</span>
              <span className="text-[10px] tracking-wider px-2 py-0.5 bg-[#F0F0F0] ml-2">
                {report?.trading_mode}
              </span>
            </DialogTitle>
          </DialogHeader>
          
          {report && (
            <div className="space-y-6 pt-4">
              {/* Account Summary */}
              <div>
                <p className="text-[10px] tracking-widest text-[#999] mb-3">ACCOUNT SUMMARY</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 border border-[#E5E5E5]">
                    <p className="text-[10px] text-[#999] mb-1">LOCAL BALANCE</p>
                    <p className="text-lg font-bold">${report.account_summary?.local_balance?.toFixed(2)}</p>
                  </div>
                  <div className="p-3 border border-[#E5E5E5]">
                    <p className="text-[10px] text-[#999] mb-1">TOTAL P/L</p>
                    <p className={`text-lg font-bold ${report.account_summary?.total_pnl >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                      ${report.account_summary?.total_pnl?.toFixed(2)}
                    </p>
                  </div>
                  <div className="p-3 border border-[#E5E5E5]">
                    <p className="text-[10px] text-[#999] mb-1">P/L %</p>
                    <p className={`text-lg font-bold ${report.account_summary?.total_pnl_percent >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                      {report.account_summary?.total_pnl_percent?.toFixed(2)}%
                    </p>
                  </div>
                </div>
                
                {/* Exchange Balances */}
                <div className="mt-3 grid grid-cols-2 gap-3">
                  {Object.entries(report.account_summary?.exchange_balances || {}).map(([ex, bal]) => (
                    <div key={ex} className="p-3 border border-[#E5E5E5]">
                      <p className="text-[10px] text-[#999] mb-1">{ex.toUpperCase()} BALANCE</p>
                      {bal.error ? (
                        <p className="text-xs text-[#999]">Not connected or unavailable</p>
                      ) : (
                        <p className="text-sm font-mono">${bal.total?.toFixed(2)} (Free: ${bal.free?.toFixed(2)})</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Trading Statistics */}
              <div>
                <p className="text-[10px] tracking-widest text-[#999] mb-3">TRADING STATISTICS</p>
                <div className="grid grid-cols-4 gap-3">
                  <div className="p-3 border border-[#E5E5E5]">
                    <p className="text-[10px] text-[#999] mb-1">TOTAL TRADES</p>
                    <p className="text-lg font-bold">{report.trading_statistics?.total_trades}</p>
                  </div>
                  <div className="p-3 border border-[#E5E5E5]">
                    <p className="text-[10px] text-[#999] mb-1">WIN RATE</p>
                    <p className="text-lg font-bold text-emerald-600">{report.trading_statistics?.win_rate?.toFixed(1)}%</p>
                  </div>
                  <div className="p-3 border border-[#E5E5E5]">
                    <p className="text-[10px] text-[#999] mb-1">PROFIT FACTOR</p>
                    <p className="text-lg font-bold">{report.trading_statistics?.profit_factor?.toFixed(2)}</p>
                  </div>
                  <div className="p-3 border border-[#E5E5E5]">
                    <p className="text-[10px] text-[#999] mb-1">OPEN POSITIONS</p>
                    <p className="text-lg font-bold">{report.trading_statistics?.open_positions}</p>
                  </div>
                  <div className="p-3 border border-[#E5E5E5]">
                    <p className="text-[10px] text-[#999] mb-1">WINNING</p>
                    <p className="text-sm font-medium text-emerald-600">{report.trading_statistics?.winning_trades} trades</p>
                  </div>
                  <div className="p-3 border border-[#E5E5E5]">
                    <p className="text-[10px] text-[#999] mb-1">LOSING</p>
                    <p className="text-sm font-medium text-red-600">{report.trading_statistics?.losing_trades} trades</p>
                  </div>
                  <div className="p-3 border border-[#E5E5E5]">
                    <p className="text-[10px] text-[#999] mb-1">AVG WIN</p>
                    <p className="text-sm font-mono text-emerald-600">${report.trading_statistics?.average_win?.toFixed(2)}</p>
                  </div>
                  <div className="p-3 border border-[#E5E5E5]">
                    <p className="text-[10px] text-[#999] mb-1">AVG LOSS</p>
                    <p className="text-sm font-mono text-red-600">${report.trading_statistics?.average_loss?.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              {/* Market Positions for Selected Pairs */}
              {report.market_positions?.length > 0 && (
                <div>
                  <p className="text-[10px] tracking-widest text-[#999] mb-3">MARKET POSITIONS ({report.trading_pairs?.join(", ")})</p>
                  <div className="space-y-2">
                    {report.market_positions.filter(p => !p.type).map((pos, i) => (
                      <div key={i} className="p-3 border border-[#E5E5E5]">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{pos.symbol}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 ${
                              pos.trend === "bullish" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"
                            }`}>
                              {pos.trend?.toUpperCase()}
                            </span>
                          </div>
                          <span className="text-xs text-[#999]">{pos.exchange}</span>
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-xs">
                          <div>
                            <p className="text-[10px] text-[#999]">PRICE</p>
                            <p className="font-mono">${pos.current_price?.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-[#999]">24H CHANGE</p>
                            <p className={`font-mono ${pos.change_24h >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                              {pos.change_24h >= 0 ? "+" : ""}{pos.change_24h?.toFixed(2)}%
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] text-[#999]">EMA 20</p>
                            <p className="font-mono">${pos.ema_20?.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-[#999]">24H RANGE</p>
                            <p className="font-mono text-[10px]">${pos.low_24h?.toLocaleString()} - ${pos.high_24h?.toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {/* Real Open Positions */}
                    {report.market_positions.filter(p => p.type === 'position').length > 0 && (
                      <div className="mt-3 pt-3 border-t border-[#E5E5E5]">
                        <p className="text-[10px] tracking-widest text-[#999] mb-2">EXCHANGE OPEN POSITIONS</p>
                        {report.market_positions.filter(p => p.type === 'position').map((pos, i) => (
                          <div key={i} className="flex items-center justify-between p-2 border border-[#E5E5E5] text-xs mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{pos.symbol}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 ${
                                pos.side === "long" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"
                              }`}>
                                {pos.side?.toUpperCase()}
                              </span>
                              <span className="text-[10px] text-[#999]">{pos.leverage}x</span>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className="font-mono">${pos.entry_price?.toFixed(2)} → ${pos.mark_price?.toFixed(2)}</span>
                              <span className={`font-mono font-medium ${pos.unrealized_pnl >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                                ${pos.unrealized_pnl?.toFixed(2)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Connection Errors */}
              {report.connection_errors?.length > 0 && (
                <div className="p-3 bg-red-50 border border-red-200">
                  <p className="text-[10px] tracking-widest text-red-600 mb-2">CONNECTION ERRORS</p>
                  <div className="text-xs text-red-600 space-y-1">
                    {report.connection_errors.map((err, i) => (
                      <p key={i}>• {err}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Symbol Performance */}
              {Object.keys(report.symbol_performance || {}).length > 0 && (
                <div>
                  <p className="text-[10px] tracking-widest text-[#999] mb-3">PERFORMANCE BY SYMBOL</p>
                  <div className="space-y-2">
                    {Object.entries(report.symbol_performance).map(([symbol, data]) => (
                      <div key={symbol} className="flex items-center justify-between p-3 border border-[#E5E5E5]">
                        <span className="font-medium">{symbol}</span>
                        <div className="flex items-center gap-4 text-xs">
                          <span>{data.trades} trades</span>
                          <span>{data.wins} wins</span>
                          <span className={data.pnl >= 0 ? "text-emerald-600" : "text-red-600"}>
                            ${data.pnl?.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Trades */}
              {report.recent_trades?.length > 0 && (
                <div>
                  <p className="text-[10px] tracking-widest text-[#999] mb-3">RECENT TRADES</p>
                  <div className="space-y-2">
                    {report.recent_trades.map((t, i) => (
                      <div key={i} className="flex items-center justify-between p-2 border border-[#E5E5E5] text-xs">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{t.symbol}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 ${
                            t.side === "long" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"
                          }`}>
                            {t.side?.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          {t.result && (
                            <span className={`text-[10px] px-1.5 py-0.5 ${
                              t.result === "WIN" ? "bg-emerald-500 text-white" : "bg-red-500 text-white"
                            }`}>
                              {t.result}
                            </span>
                          )}
                          <span className={t.pnl >= 0 ? "text-emerald-600" : "text-red-600"}>
                            ${t.pnl?.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="text-[10px] text-[#999] pt-2 border-t border-[#E5E5E5]">
                Generated: {new Date(report.generated_at).toLocaleString()}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
